/**
 * HU ↔ SK építési szinonimák a BOQ és a RAG kereséshez.
 * A költségvetés szlovák, a chat magyar — a nyers FTS ezt nem köti össze.
 */

const SYNONYMS: Record<string, string[]> = {
  sávalap: ["savalap", "pás", "pas", "pásov", "pasov", "základových", "zakladovych", "zakladanie", "základanie"],
  alapsáv: ["alapsav", "pás", "pas", "pásov", "pasov", "základových", "zakladovych"],
  alapozás: ["alapozas", "zakladanie", "základanie", "základ", "zaklad"],
  beton: ["betón", "beton", "betónu", "betonu"],
  vasalt: ["železový", "zelezovy", "železovy", "výstuže", "vystuze"],
  mennyiség: ["mnozstvo", "množstvo", "quantity", "mennyiseg"],
  költség: ["koltség", "cena", "náklad", "naklad", "rozpočet", "rozpocet", "becslés", "becsles"],
  földmunka: ["foldmunka", "zemné", "zemne", "výkop", "vykop"],
  hidroizoláció: ["hidroizolacio", "hydroizolácia", "hydroizolacia", "hydrobit"],
  falazás: ["falazas", "murovanie", "tehla", "tvárnica", "tvarnica"],
  tető: ["teto", "strecha", "krov", "krytina"],
  vakolat: ["omietka", "omietanie"],
  betonozás: ["betonozas", "betónovanie", "betonovanie"]
};

/** Ékezetek levágása kereséshez (NFD + combining marks). */
export function foldDiacritics(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase();
}

const STOP = new Set([
  "a",
  "az",
  "egy",
  "es",
  "és",
  "vagy",
  "hogy",
  "van",
  "volt",
  "lesz",
  "kell",
  "nem",
  "igen",
  "mit",
  "mi",
  "milyen",
  "mennyi",
  "hol",
  "mikor",
  "hogyan",
  "ezt",
  "azt",
  "ez",
  "arra",
  "erre",
  "valahol",
  "projekt",
  "projektben",
  "dokumentum",
  "dokumentumban",
  "fajl",
  "fájl",
  "irja",
  "írja",
  "latod",
  "látod",
  "tudod",
  "kerlek",
  "kérlek",
  // Követő kérdések / zaj — a "most" pl. LIKE '%most%'-tal félremegy
  "most",
  "utan",
  "után",
  "mar",
  "már",
  "meg",
  "csak",
  "igy",
  "így",
  "ugye",
  "akkor",
  "tehat",
  "tehát",
  "ami",
  "amely",
  "amiatt",
  "adatot",
  "adat",
  "sorokat",
  "sorok",
  "ertekeket",
  "értékeket",
  "latni",
  "látni",
  "ujraindexeles",
  "újraindexelés",
  "ujraindexelés",
  "indexeles",
  "indexelés",
  "indexelve",
  "forras",
  "forrás",
  "forrasok",
  "források",
  "reszletek",
  "részletek",
  "pedig",
  "viszont",
  "szeretnek",
  "szeretnék",
  "rajonni",
  "rájönni",
  "hibara",
  "hibára",
  "kerdes",
  "kérdés"
]);

const DOMAIN_KEYS = Object.keys(SYNONYMS).map((k) => foldDiacritics(k));

function isDomainToken(w: string): boolean {
  if (DOMAIN_KEYS.some((k) => w === k || w.startsWith(k) || k.startsWith(w))) return true;
  for (const alts of Object.values(SYNONYMS)) {
    for (const a of alts) {
      const af = foldDiacritics(a);
      if (w === af || w.startsWith(af) || af.startsWith(w)) return true;
    }
  }
  return false;
}

function tokenize(raw: string): string[] {
  return foldDiacritics(raw)
    .replace(/[%_\\,()?!.;:"""„”]/g, " ")
    .split(/\s+/)
    .map((w) => w.trim())
    .filter((w) => {
      if (STOP.has(w)) return false;
      if (w.length < 3) return false;
      // Rövid, nem szakszavak zajosak a LIKE keresésben („most”, „van”…)
      if (w.length < 4 && !isDomainToken(w)) return false;
      return true;
    });
}

/** A kérdésből kulcsszavak + szinonimák (szóközzel elválasztva a BOQ RPC-nek). */
export function expandSearchQuery(raw: string): string {
  const words = tokenize(raw);
  const out = new Set<string>();
  for (const w of words) {
    out.add(w);
    for (const [key, alts] of Object.entries(SYNONYMS)) {
      const keyFold = foldDiacritics(key);
      if (w === keyFold || w.startsWith(keyFold) || keyFold.startsWith(w)) {
        for (const a of alts) out.add(foldDiacritics(a));
        out.add(keyFold);
      }
      for (const a of alts) {
        const af = foldDiacritics(a);
        if (w === af || w.startsWith(af) || af.startsWith(w)) {
          out.add(keyFold);
          for (const x of alts) out.add(foldDiacritics(x));
        }
      }
    }
  }
  return [...out].join(" ").slice(0, 400);
}

/**
 * Követő kérdéshez: az aktuális üzenet + az utolsó user üzenetek.
 * „és most látod?” önmagában nem tartalmazza a beton/sávalap kulcsszót.
 */
export function buildRetrievalQuery(
  current: string,
  history: ReadonlyArray<{ role: string; content: string }> = []
): string {
  const priorUser = history
    .filter((m) => m.role === "user")
    .map((m) => m.content.trim())
    .filter(Boolean)
    .slice(-3);
  const parts = [...priorUser, current.trim()].filter(Boolean);
  return parts.join("\n").slice(0, 800);
}

/** Mennyiség / költség jellegű kérdés-e (BOQ prioritás) — history-vel együtt. */
export function isQuantityOrCostQuestion(raw: string): boolean {
  const f = foldDiacritics(raw);
  return /\b(mennyi|mennyiseg|m3|m²|m2|m³|beton|savalap|alapsav|alapozas|koltseg|ar|egysegar|tonna|kg|fm|bm|ks|darab|betonoz|vasal|anyag|zakladanie|pasov)\b/i.test(
    f
  );
}
